import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import classNames from "classnames"

import {
  OnlineStatusReportDevice,
  OnlineStatusReportPeriod,
  OnlineStatusReportSummary,
  useCreateOnlineStatusReportMutation,
  useLazyGetOnlineStatusReportDevicesQuery,
  useLazyGetOnlineStatusReportSummaryQuery,
} from "store/api/onlineStatusReportApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import { ReactComponent as IconSearch } from "assets/svg/icon-magnifying-glass.svg"
import { ReactComponent as IconLock } from "assets/svg/lock-closed.svg"

import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import {
  getDateRangeParams,
  getFilterValueFromParams,
} from "components/common/DateRangeTabsFilter/dateRangeFilterUtils"
import { Spinner } from "components/common/Spinner"
import { ButtonNew } from "components/form/ButtonNew"

import { ReportTable } from "./reportTable"
import { adHocReportHeaderCells } from "./tableConfig"

import styles from "./index.module.scss"

type Period = "week" | "month"
type ReportType = "online-status" | "engagement"

export const hasEngagementReportAccess = (subscriptionPlan?: SubscriptionPlan) =>
  subscriptionPlan === SubscriptionPlan.E_PLUS ||
  subscriptionPlan === SubscriptionPlan.NETWORK ||
  subscriptionPlan === SubscriptionPlan.NETWORK_JULY_2026

const toReportPeriod = (period: Period): OnlineStatusReportPeriod => (period === "week" ? "last_week" : "last_month")

const getPeriodFromParams = (params: Record<string, string>): Period =>
  getFilterValueFromParams(params, "report_period") === "last-month" ? "month" : "week"

const getReportTableRows = (devices: OnlineStatusReportDevice[], currentPage: number, perPage: number) =>
  devices.map((row, index) => ({
    sn: { value: row.sn },
    customer_email: { value: row.customer_email, csvValue: row.customer_email ?? "N/A" },
    status: { value: row.status },
    online_percentage: { value: row.online_percentage, csvValue: row.online_percentage ?? "N/A" },
    address: { value: row.address, csvValue: row.address ?? "N/A" },
    thermostat_name: { value: row.thermostat_name, csvValue: row.thermostat_name ?? "N/A" },
    rowSettings: { id: (currentPage - 1) * perPage + index, item: row },
  })) as UITypes.TableBodyRow[]

export const buildEngagementReportUrl = ({
  origin,
  reportingPeriodFrom,
  reportingPeriodTo,
  reportingPeriodType,
}: {
  origin: string
  reportingPeriodFrom: string
  reportingPeriodTo: string
  reportingPeriodType: string
}) => {
  const reportUrl = new URL("/engagement-report", origin)
  reportUrl.searchParams.set("reporting_period_from", reportingPeriodFrom)
  reportUrl.searchParams.set("reporting_period_to", reportingPeriodTo)
  reportUrl.searchParams.set("reporting_period_type", reportingPeriodType)

  return reportUrl.toString()
}

const AdHocReportsPage = () => {
  const isAdHocReportsEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.AdHocReports))
  const userProfile = useAppSelector(state => state.user.userProfile)
  const isEngagementReportEnabled = hasEngagementReportAccess(userProfile?.contractor?.subscription_plan)
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const navigate = useNavigate()
  const [reportType, setReportType] = useState<ReportType>("online-status")
  const [engagementReportUrl, setEngagementReportUrl] = useState<string | null>(null)
  const [reportId, setReportId] = useState<string | null>(null)
  const [reportState, setReportState] = useState<"empty" | "preparing" | "complete" | "failed">("empty")
  const [createReport, { isLoading: isCreating }] = useCreateOnlineStatusReportMutation()
  const [fetchSummary] = useLazyGetOnlineStatusReportSummaryQuery()
  const [fetchDevices, { data: devicesData, isFetching: isFetchingDevices }] =
    useLazyGetOnlineStatusReportDevicesQuery()
  const [summaryData, setSummaryData] = useState<OnlineStatusReportSummary>()
  const timeoutRef = useRef<number | null>(null)

  const clearPollingTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  useEffect(() => {
    const defaultParams: Record<string, string | null> = {}
    if (!urlQueryParams.report_period_start && !urlQueryParams.report_period_end) {
      Object.assign(defaultParams, getDateRangeParams("last-week", "report_period"))
    }
    if (!urlQueryParams.engagement_period_start && !urlQueryParams.engagement_period_end) {
      Object.assign(defaultParams, getDateRangeParams("week-to-date", "engagement_period"))
    }
    if (Object.keys(defaultParams).length > 0) setUrlQueryParams(defaultParams)
  }, [])

  const currentPage = Number(urlQueryParams.page) || 1
  const perPage = Number(urlQueryParams.perPage) || 15
  const orderColumn = urlQueryParams.orderColumn || "sn"
  const orderType = urlQueryParams.orderType === "DESC" ? "DESC" : "ASC"
  const reportPeriod = summaryData?.reporting_period

  const fetchReport = useCallback(
    async (id: string, retryAfterSeconds = 2) => {
      try {
        const summary = await fetchSummary(id).unwrap()
        if (summary.state === "preparing") {
          setReportState("preparing")
          timeoutRef.current = window.setTimeout(
            () => void fetchReport(id, summary.retry_after_seconds ?? retryAfterSeconds),
            (summary.retry_after_seconds ?? retryAfterSeconds) * 1000,
          )
          return
        }

        setSummaryData(summary)
        setReportState("complete")
      } catch {
        setReportState("failed")
      }
    },
    [fetchSummary],
  )

  useEffect(() => clearPollingTimeout, [clearPollingTimeout])

  const handleGenerate = async () => {
    clearPollingTimeout()
    setReportState("preparing")
    setSummaryData(undefined)

    try {
      const response = await createReport({ period: toReportPeriod(getPeriodFromParams(urlQueryParams)) }).unwrap()
      setReportId(response.report_id)

      if (response.state === "complete") {
        const summary = await fetchSummary(response.report_id).unwrap()
        setSummaryData(summary)
        setReportState("complete")
      } else {
        await fetchReport(response.report_id, response.retry_after_seconds ?? 2)
      }
    } catch {
      setReportState("failed")
    }
  }

  const handleGenerateEngagementReport = () => {
    const start = urlQueryParams.engagement_period_start
    const end = urlQueryParams.engagement_period_end
    if (!start || !end || !isEngagementReportEnabled) return

    setEngagementReportUrl(
      buildEngagementReportUrl({
        origin: window.location.origin,
        reportingPeriodFrom: start,
        reportingPeriodTo: end,
        reportingPeriodType: getFilterValueFromParams(urlQueryParams, "engagement_period"),
      }),
    )
  }

  useEffect(() => {
    if (!reportId || reportState !== "complete") return

    void fetchDevices({
      reportId,
      params: {
        sn: urlQueryParams.sn,
        customer_email: urlQueryParams.customer_email,
        address: urlQueryParams.address,
        thermostat_name: urlQueryParams.thermostat_name,
        order_column: orderColumn,
        order_type: orderType,
        page: currentPage,
        per_page: perPage,
      },
    })
  }, [currentPage, fetchDevices, orderColumn, orderType, perPage, reportId, reportState, urlQueryParams])

  const tableRows = useMemo(
    () => getReportTableRows(devicesData?.data ?? [], currentPage, perPage),
    [currentPage, devicesData?.data, perPage],
  )

  const { handleLocalExport } = useExportCSV({
    params: {},
    headerCells: adHocReportHeaderCells,
    fileName: "thermostat_online_status_report",
  })

  if (!isAdHocReportsEnabled) return <Navigate to="/" replace />

  const isPreparing = isCreating || reportState === "preparing"
  const hasReport = reportState === "complete" && !!devicesData
  const isEngagement = reportType === "engagement"
  const isShowingEngagementPageIframe = Boolean(isEngagement && isEngagementReportEnabled && engagementReportUrl)

  return (
    <>
      <div className={styles.headerCard}>
        <div className={styles.reportTabs} role="tablist" aria-label="Report type">
          <button
            className={styles.reportTab}
            type="button"
            role="tab"
            aria-selected={isEngagement}
            onClick={() => setReportType("engagement")}
          >
            Engagement report
          </button>
          <button
            className={styles.reportTab}
            type="button"
            role="tab"
            aria-selected={!isEngagement}
            onClick={() => setReportType("online-status")}
          >
            Thermostat online status
          </button>
        </div>
        <h1 className={styles.title}>{isEngagement ? "Engagement report" : "Thermostat online status report"}</h1>
        <p className={styles.subtitle}>
          {isEngagement
            ? "How customers engaged with you this month, across thermostats and the mobile app."
            : "View online percentages for thermostats assigned to your account."}
        </p>
        <div className={styles.controls}>
          {isEngagement ? (
            <DateRangeTabsFilter
              paramName="engagement_period"
              urlQueryParams={urlQueryParams}
              onParamsChange={setUrlQueryParams}
              filterGroups={[["week-to-date"], ["month-to-date"], ["custom"]]}
              maxRangeInYears={1}
            />
          ) : (
            <DateRangeTabsFilter
              paramName="report_period"
              urlQueryParams={urlQueryParams}
              onParamsChange={setUrlQueryParams}
              filterGroups={[["last-week"], ["last-month"]]}
            />
          )}
          <ButtonNew
            color="blue"
            onClick={isEngagement ? handleGenerateEngagementReport : handleGenerate}
            disabled={isEngagement ? !isEngagementReportEnabled : isPreparing}
          >
            Generate report
          </ButtonNew>
        </div>
      </div>
      <ContentWrapper className={classNames({ [styles.iframeContentWrapper]: isShowingEngagementPageIframe })}>
        <div className={styles.reportCard}>
          {isEngagement && !isEngagementReportEnabled && (
            <div className={styles.empty}>
              <div className={styles.iconWrapper}>
                <IconLock className={styles.lockIcon} />
              </div>
              <strong>Engagement report isn&apos;t included in your plan</strong>
              <ButtonNew color="blueBordered" onClick={() => navigate("/account/billing")}>
                View Plans
              </ButtonNew>
            </div>
          )}
          {isEngagement &&
            isEngagementReportEnabled &&
            (engagementReportUrl ? (
              <iframe
                key={engagementReportUrl}
                className={styles.engagementReportFrame}
                src={engagementReportUrl}
                title="Engagement report"
              />
            ) : (
              <div className={styles.empty}>
                <div className={styles.iconWrapper}>
                  <IconSearch />
                </div>
                <strong>No report yet</strong>
                <span>
                  Choose Generate report to see this month&apos;s engagement, top devices, partner activity and customer
                  calls. Figures are current as of the moment you generate.
                </span>
              </div>
            ))}
          {!isEngagement && isPreparing && (
            <div className={styles.generating} role="status">
              <div className={styles.generatingMessage}>
                <Spinner />
                Please wait while the report is getting generated
              </div>
            </div>
          )}
          {!isEngagement && reportState === "failed" && (
            <div className={styles.errorMessage}>We couldn&apos;t generate this report. Please try again.</div>
          )}
          {!isEngagement && !isPreparing && reportState === "empty" && (
            <div className={styles.empty}>
              <div className={styles.iconWrapper}>
                <IconSearch />
              </div>
              <strong>No report yet</strong>
              <span>
                Pick a date range above and choose Generate report to see online percentages for your installed
                thermostats.
              </span>
            </div>
          )}
          {!isEngagement && !isPreparing && hasReport && (
            <ReportTable
              rows={tableRows}
              meta={devicesData.meta}
              isLoading={isFetchingDevices}
              onExport={() => handleLocalExport(tableRows)}
              canExport={Boolean(devicesData?.data.length)}
              periodLabel={reportPeriod?.display_label}
              averageOnlinePercentage={summaryData?.overall_online_percentage}
            />
          )}
        </div>
      </ContentWrapper>
    </>
  )
}

export default AdHocReportsPage
